import Alert from '@mui/material/Alert';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { FREQUENCY_CAPPING_ALERT_MAX_WIDTH } from '@openx/components-form/FrequencyCapping/constants.ts';
import type { DealApi } from '@openx/types/deal.ts';
import { FREQUENCY_CAPPING_FIELDS } from '@openx/types/frequencyCapping.ts';
import { formatFrequencyCappingValue, getFrequencyCappingTitle } from '@openx/utils/lib/frequencyCappingUtils.ts';
import capitalize from 'lodash/capitalize';
import { useTranslation } from 'react-i18next';

import type { DealDetailsSectionProps } from '../../../../types.ts';

export type FrequencyCappingDetailsDeal = Pick<DealApi, 'frequency_capping'>;

export function FrequencyCappingDetailsSection<T extends FrequencyCappingDetailsDeal>({
  deal,
  historyMode,
}: Omit<DealDetailsSectionProps<T>, 'showPacing'>) {
  const { t } = useTranslation();
  const { frequency_capping } = deal;

  return (
    <FormSection divider historyMode={historyMode} title={t('Frequency Capping')}>
      {frequency_capping?.length ? (
        <>
          <Alert sx={{ marginBottom: 1, maxWidth: FREQUENCY_CAPPING_ALERT_MAX_WIDTH }} severity="info">
            {t('The Frequency Cap operates according to the UTC time zone.')}
          </Alert>
          {frequency_capping.map((cap, index) => {
            const impressions = cap[FREQUENCY_CAPPING_FIELDS.IMPRESSIONS_LIMIT];
            const periodValue = cap[FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_VALUE];
            const unit = capitalize(cap[FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_UNIT]);
            const value = formatFrequencyCappingValue({ impressions, unit, t, value: periodValue });

            return (
              <FieldDetails
                key={index}
                title={getFrequencyCappingTitle(frequency_capping.length, index, t)}
                value={value}
              />
            );
          })}
        </>
      ) : (
        <FieldDetails toggle title={t('Enable Frequency Capping')} value={false} />
      )}
    </FormSection>
  );
}
